import { ColumnDef } from '@tanstack/react-table';
import {
  Card,
  InputSelect,
  InputTags,
  Loading,
  TableListData,
} from 'components';
import { Button, InputWraper, NumberUtils, useForm } from 'modules';
import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { MainRouteKeys } from 'routes/enums';
import {
  useElectricPayBillMutation,
  useGetAgenciesQuery,
  useGetElectricBillsQuery,
  useGetBalanceQuery,
} from 'services';
import type {
  AgencyEntity,
  GetBillsEntity,
  TransactionOrder,
} from 'services/openapi';

import { ExcelReader, ModalConfirm } from '../Components';
import {
  columnsDefined,
  convertSelectOptions,
  convertVariables,
  filterOptions,
  type FilterType,
  initFilterValues,
} from './config';

const ColumnKey = 'Mã khách hàng';

const PayBillElectricFeatures = () => {
  const [variables, setVariables] = useState<FilterType>({
    ...initFilterValues,
  });

  const navigate = useNavigate();

  const [readerCustomers, setReaderCustomers] = useState<string[]>();

  const [totalAmount, setTotalAmount] = useState<number>(0);

  const [selectedRows, setSelectedRows] = useState<TransactionOrder[]>([]);

  const [dataTable, setDataTable] = useState<GetBillsEntity | null>(null);

  const [isOpen, setIsOpen] = useState<boolean>(false);

  const { data: agencies, isLoading } = useGetAgenciesQuery({
    companyId: '',
    page: 1,
    pageSize: 100,
  });

  const { mutateAsync, isLoading: isLoadingProcess } =
    useElectricPayBillMutation();

  const { refetch: refetchBalance } = useGetBalanceQuery();

  const handleFilter = useCallback(
    (e: FilterType) => {
      setVariables({ ...e, refreshData: variables.refreshData + 1 });
    },
    [variables],
  );

  const { getInputProps, handleSubmit, setValues } = useForm({
    structure: filterOptions,
    onSubmit: handleFilter,
  });

  const handleReaderCallback = (data: any) => {
    if (!data?.length) return;

    const customerCodes = data.map((item: any) => {
      if (!Object.keys(item).includes(ColumnKey)) {
        toast.error('Tệp không đúng định dạng');
        return;
      }

      return item[ColumnKey];
    });

    setReaderCustomers(customerCodes);

    setValues({ agencyCode: '' });
  };

  const handleOnSelectedRowsCallback = useCallback(
    (selectedRow: TransactionOrder[]) => {
      if (!selectedRow || !selectedRow.length) {
        setTotalAmount(0);
        return;
      }

      setSelectedRows(selectedRow);

      let total = 0;
      const customerBillsCode = [];

      for (let index = 0; index < selectedRow.length; index++) {
        const data = selectedRow[index];
        if (data) {
          customerBillsCode.push(data.customerCode);
          total += data.amount;
        }
      }

      setTotalAmount(total);
    },
    [selectedRows?.length, totalAmount],
  );

  const handleSubmitBills = async ({ pinCode }: { pinCode: string }) => {
    const customerBills = selectedRows.map((item) => item.transactionId);

    if (!customerBills.length) {
      toast.error('Vui lòng chọn hóa đơn');
      return;
    }

    try {
      const payload = {
        pinCode: pinCode,
        customerBills: customerBills,
      };

      const data = await mutateAsync(payload);

      if (data?.statusCode === 200 && data?.orderCode) {
        refetchBalance();

        toast.success('Thanh toán hoàn tất');

        navigate(`${MainRouteKeys.PayBillElectric}/${data?.orderCode}`);

        setIsOpen(false);
      }

      throw new Error('Thanh toán không thành công');
    } catch (error) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    if (dataTable?.data?.msgError) {
      toast.error(dataTable?.data?.msgError);
    }
  }, [dataTable]);

  const handleKeyDown = (event: any) => {
    if (event.keyCode === 13) {
      event.preventDefault();
    }
  };

  return (
    <>
      <div className='textRight mb20'>
        <ExcelReader
          onReaderCallback={handleReaderCallback}
          classNames='ml10'
        />

        <a
          href={`${process.env.PUBLIC_URL}/assets/templates/Ma_khach_hang.xlsx`}
          download
        >
          <Button
            label='Tải về tệp mẫu'
            type='button'
            buttonType='info-outline'
            className='ml10'
          />
        </a>
      </div>

      <Card className='container-fluid electric-bill-access mb20'>
        <form onSubmit={handleSubmit}>
          <div className='row'>
            <div className='col-lg-4 col-12'>
              {/* <Loading isLoading={isLoading}> */}
              <InputWraper
                label={'Điện lực'}
                inputProps={getInputProps('agencyCode')}
                renderInput={(props) => (
                  <InputSelect
                    {...props}
                    options={convertSelectOptions(agencies as AgencyEntity[])}
                    placeholder='Chọn điện lực'
                  />
                )}
              />
              {/* </Loading> */}
            </div>

            <div className='col-lg-4 col-12'>
              <InputWraper
                label='Mã khách hàng'
                isRequired
                inputProps={getInputProps('customerCodes')}
                renderInput={(props) => (
                  <InputTags
                    {...props}
                    defaultValue={readerCustomers}
                    // onChangeCallback={(customerCodes) => {
                    //   setVariables({ ...variables, customerCodes });
                    //   setReaderCustomers([]);
                    // }}
                    placeholder='Nhập mã khách hàng'
                    onKeyDown={handleKeyDown}
                  />
                )}
              />
            </div>
          </div>

          <div className='textCenter mt16'>
            <Button label='Truy vấn' type='submit' buttonType='success' />
          </div>
        </form>
      </Card>

      <TableListData<TransactionOrder>
        columns={columnsDefined as ColumnDef<TransactionOrder>[]}
        useQuery={useGetElectricBillsQuery}
        onSelectedRowsCallback={handleOnSelectedRowsCallback}
        variables={variables}
        setVariables={setVariables}
        convertVariables={convertVariables}
        dataQueryKey='lstBills'
        callBackData={setDataTable}
        selectable
        orderColumn
        customTopRow={() => (
          <tr>
            <td colSpan={6}></td>
            <td>
              <b>TỔNG</b>
            </td>
            <td>
              <b>{NumberUtils.toFormatNumber(Number(totalAmount))}</b>
            </td>
          </tr>
        )}
      />

      {!!selectedRows?.length && (
        <div className='textCenter mt20'>
          <Button
            label={'Thanh toán'}
            type='submit'
            buttonType='success'
            onClick={() => setIsOpen(true)}
          />
        </div>
      )}

      <ModalConfirm
        isOpen={isOpen}
        onSuccess={handleSubmitBills}
        onClose={() => setIsOpen(false)}
        isLoadingProcess={isLoadingProcess}
      />
    </>
  );
};

export default PayBillElectricFeatures;
